import {
  FillRule,
  GridDraft,
  GridProduct,
  LevelDraft,
  PricingTier,
  PricingTierGridPayload,
  ProductDraft,
  ProposedGrid,
} from "./types";

const MIN_LEVELS = 2;

let counter = 0;
const nextKey = (prefix: string) => {
  counter += 1;
  return `${prefix}-${counter}`;
};

export const levelName = (level: LevelDraft, index: number): string =>
  level.label.trim() === "" ? String(index + 1) : level.label.trim();

export const newLevel = (label = ""): LevelDraft => ({
  key: nextKey("level"),
  label,
});

const toProductDraft = (product: GridProduct): ProductDraft => ({
  uh_cat: product.uh_cat,
  short_name: product.short_name,
  uh_type_name: product.uh_type_name,
  prices: {},
});

export const toDraft = (
  tiers: PricingTier[],
  products: GridProduct[]
): GridDraft => {
  const ordered = [...tiers].sort((left, right) => left.level - right.level);
  const levels = ordered.map((tier) => newLevel(tier.label ?? ""));

  const rows = new Map(
    products.map((product) => [product.uh_cat, toProductDraft(product)])
  );
  ordered.forEach((tier, index) => {
    const levelKey = levels[index]?.key;
    if (!levelKey) return;
    tier.prices.forEach((cell) => {
      const row =
        rows.get(cell.uh_cat) ??
        toProductDraft({
          uh_cat: cell.uh_cat,
          short_name: cell.uh_cat_short_name ?? String(cell.uh_cat),
          uh_type_name: null,
        });
      row.prices[levelKey] = String(Number(cell.price_gross));
      rows.set(cell.uh_cat, row);
    });
  });

  return { levels, products: [...rows.values()] };
};

export const isPriced = (
  product: ProductDraft,
  levels: LevelDraft[]
): boolean =>
  levels.every((level) => (product.prices[level.key] ?? "").trim() !== "");

export const isUntouched = (
  product: ProductDraft,
  levels: LevelDraft[]
): boolean =>
  levels.every((level) => (product.prices[level.key] ?? "").trim() === "");

export const pricedProducts = (draft: GridDraft): ProductDraft[] =>
  draft.products.filter((product) => isPriced(product, draft.levels));

export const hasAnyPrice = (draft: GridDraft): boolean =>
  draft.products.some((product) =>
    Object.values(product.prices).some((price) => price.trim() !== "")
  );

export const toPayload = (
  draft: GridDraft
): Pick<PricingTierGridPayload, "tiers"> => {
  const priced = pricedProducts(draft);
  return {
    tiers: draft.levels.map((level, index) => ({
      level: index + 1,
      label: level.label.trim() === "" ? null : level.label.trim(),
      prices: priced.map((product) => ({
        uh_cat: product.uh_cat,
        price_gross: Number(product.prices[level.key]).toFixed(2),
      })),
    })),
  };
};

export const validateDraft = (draft: GridDraft): string | null => {
  if (draft.levels.length < MIN_LEVELS) {
    return "PRICING_TIER_ERROR_MIN_LEVELS";
  }

  const priced = pricedProducts(draft);
  if (priced.length === 0) {
    return "PRICING_TIER_ERROR_NO_PRICED_ROW";
  }
  // The backend needs every level to price the same categories. Said here
  // rather than let the save fail on a rule the analyst cannot see.
  if (
    draft.products.some(
      (product) =>
        !isPriced(product, draft.levels) && !isUntouched(product, draft.levels)
    )
  ) {
    return "PRICING_TIER_ERROR_PARTIAL_ROW";
  }

  for (const product of priced) {
    const prices = draft.levels.map((level) =>
      Number(product.prices[level.key])
    );
    if (prices.some((price) => !Number.isFinite(price) || price <= 0)) {
      return "PRICING_TIER_ERROR_INVALID_PRICE";
    }
    // A price may stay flat across levels but never fall as the level rises.
    if (
      prices.some(
        (price, index) => index > 0 && price < (prices[index - 1] ?? 0)
      )
    ) {
      return "PRICING_TIER_ERROR_COLUMN_FALLING";
    }
  }
  return null;
};

// Existing cells outside the range are kept.
export const applyFillRule = (
  product: ProductDraft,
  levels: LevelDraft[],
  rule: FillRule
): ProductDraft => {
  const prices = { ...product.prices };
  let value = rule.start;
  for (let index = rule.fromIndex; index <= rule.toIndex; index += 1) {
    const level = levels[index];
    if (!level) continue;
    prices[level.key] = String(Math.round(value * 100) / 100);
    value =
      rule.unit === "percent"
        ? value * (1 + rule.step / 100)
        : value + rule.step;
  }
  return { ...product, prices };
};

/**
 * Excel puts a copied range on the clipboard as tab-separated text, so pasting
 * needs no file, no upload and no workbook parser.
 */
const normaliseNumber = (raw: string): string | null => {
  const cleaned = raw.replace(/[\s  ]/g, "").replace(/[^\d,.-]/g, "");
  if (cleaned === "") return null;
  // The last separator is the decimal one; anything before it is a grouping mark.
  const lastComma = cleaned.lastIndexOf(",");
  const lastDot = cleaned.lastIndexOf(".");
  const decimalAt = Math.max(lastComma, lastDot);
  const normalised =
    decimalAt === -1
      ? cleaned
      : `${cleaned.slice(0, decimalAt).replace(/[.,]/g, "")}.${cleaned.slice(
          decimalAt + 1
        )}`;
  const value = Number(normalised);
  return Number.isFinite(value) ? String(value) : null;
};

/**
 * The grid as a spreadsheet block, headers included so the Excel round trip
 * closes. `stripHeaders` takes them off on the way back in.
 */
export const gridToClipboard = (draft: GridDraft): string => {
  const header = ["", ...draft.levels.map(levelName)];
  const rows = draft.products.map((product) => [
    product.uh_type_name
      ? `${product.uh_type_name} \u2014 ${product.short_name}`
      : product.short_name,
    ...draft.levels.map((level) => product.prices[level.key] ?? ""),
  ]);
  return [header, ...rows].map((line) => line.join("\t")).join("\n");
};

/**
 * Detection rests on the corner cell, which `gridToClipboard` leaves empty.
 * Looking for numbers cannot work, a level being named "Niveau -1" or by its
 * bare rank.
 */
export const stripHeaders = (block: string[][]): string[][] => {
  const first = block[0];
  if (block.length < 2 || !first || first.length < 2 || first[0] !== "") {
    return block;
  }
  return block.slice(1).map((line) => line.slice(1));
};

export const parseClipboard = (text: string): string[][] =>
  text
    .replace(/\r\n?/g, "\n")
    .replace(/\n$/, "")
    .split("\n")
    .map((line) => line.split("\t").map((cell) => cell.trim()));

export const pasteInto = (
  draft: GridDraft,
  block: string[][],
  anchor: { productIndex: number; levelIndex: number }
): GridDraft => {
  const products = draft.products.map((product) => ({
    ...product,
    prices: { ...product.prices },
  }));

  block.forEach((line, row) => {
    const product = products[anchor.productIndex + row];
    if (!product) return;
    line.forEach((raw, column) => {
      const level = draft.levels[anchor.levelIndex + column];
      if (!level) return;
      const value = normaliseNumber(raw);
      if (value !== null) {
        product.prices[level.key] = value;
      }
    });
  });

  return { ...draft, products };
};

/** Excel hands over locale-formatted numbers: "1 234,50", "1,234.50", "48 €". */

/** Per cell rather than per grid, so the message says where. */
export const cellError = (
  product: ProductDraft,
  levels: LevelDraft[],
  index: number
): string | null => {
  const level = levels[index];
  if (!level) return null;

  const raw = (product.prices[level.key] ?? "").trim();
  if (raw === "") {
    // A row left entirely blank is simply not priced by this grid.
    return isUntouched(product, levels)
      ? null
      : "PRICING_TIER_ERROR_CELL_EMPTY";
  }

  const value = Number(raw);
  if (!Number.isFinite(value) || value <= 0) {
    return "PRICING_TIER_ERROR_CELL_INVALID";
  }

  const previousLevel = levels[index - 1];
  const previous = previousLevel
    ? Number(product.prices[previousLevel.key])
    : null;
  if (previous !== null && Number.isFinite(previous) && value < previous) {
    return "PRICING_TIER_ERROR_CELL_FALLING";
  }
  return null;
};

/** `steps[product][level]`, the rise from the tier below, null on the first. */
export const stepsOf = (draft: GridDraft): (number | null)[][] =>
  draft.products.map((product) =>
    draft.levels.map((level, index) => {
      const previous = index === 0 ? null : draft.levels[index - 1];
      if (!previous) return null;
      const before = Number(product.prices[previous.key]);
      const after = Number(product.prices[level.key]);
      if (!Number.isFinite(before) || !Number.isFinite(after) || before <= 0) {
        return null;
      }
      return ((after - before) / before) * 100;
    })
  );

// `nights` alone does not say it: a property under the sample the percentiles
// need has nights and still no tier.
export const proposalIsEmpty = (proposal: ProposedGrid): boolean =>
  proposal.columns.every((column) => column.prices.length === 0);

export const draftFromProposedGrid = (proposal: ProposedGrid): GridDraft => {
  // A cell is keyed by its LEVEL, not by its rank, so the prices hang off the
  // very objects the grid renders.
  const levels = Array.from({ length: proposal.levels }, () => newLevel());
  return {
    levels,
    products: proposal.columns.map((column) => ({
      uh_cat: column.uh_cat,
      short_name: column.short_name,
      uh_type_name: column.uh_type_name,
      prices: Object.fromEntries(
        column.prices.flatMap((price, index) => {
          const level = levels[index];
          return level ? [[level.key, String(Number(price))]] : [];
        })
      ),
    })),
  };
};
